import { useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/dates";

type Notification = {
  id: number;
  kind: string;
  payload: Record<string, unknown>;
  link: string | null;
  createdAt: string;
  readAt: string | null;
};

type Result = { items: Notification[]; unreadCount: number };

export function NotificationBell({ collapsed = false }: { collapsed?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const q = useQuery<Result>({
    queryKey: ["/api/notifications", { unreadOnly: false }],
    queryFn: () => fetch("/api/notifications", { credentials: "include" }).then((r) => r.json()),
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: async (id: number) => {
      await fetch(`/api/notifications/${id}/read`, { method: "PATCH", credentials: "include" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });
  const markAllRead = useMutation({
    mutationFn: async () => {
      await fetch("/api/notifications/mark-all-read", { method: "POST", credentials: "include" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/notifications"] }),
  });

  const unreadCount = q.data?.unreadCount ?? 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications — ${unreadCount} unread`}
          className={cn(
            "relative flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
            collapsed ? "size-8" : "size-8 px-2",
          )}
        >
          <BellIcon className="size-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-destructive text-[9px] font-semibold text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
          <p className="font-display text-sm font-semibold">Inbox</p>
          {unreadCount > 0 && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {q.isLoading && <p className="p-4 text-xs text-muted-foreground">Loading…</p>}
          {q.data && q.data.items.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">No notifications. You're caught up.</p>
          )}
          {q.data && q.data.items.length > 0 && (
            <ul className="divide-y divide-border/40">
              {q.data.items.slice(0, 20).map((n) => (
                <li key={n.id} className={cn("px-3 py-2", !n.readAt && "bg-accent/30")}>
                  {n.link ? (
                    <Link href={n.link} onClick={() => { markRead.mutate(n.id); setOpen(false); }} className="block">
                      <NotificationItem n={n} />
                    </Link>
                  ) : (
                    <button type="button" onClick={() => markRead.mutate(n.id)} className="block w-full text-left">
                      <NotificationItem n={n} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-border px-3 py-2">
          <Link href="/notifications" onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:underline">
            View all →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({ n }: { n: Notification }) {
  return (
    <>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{n.kind}</p>
      <p className="mt-0.5 text-sm">
        {(n.payload.summary as string | undefined) ?? JSON.stringify(n.payload).slice(0, 80)}
      </p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{formatDateTime(n.createdAt)}</p>
    </>
  );
}
